import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Menu, Transition } from "@headlessui/react";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { useDispatch, useSelector } from "react-redux";
import { ResourceError } from "../../../components/utils/ResourceError";
import { PMTabs } from "../../../components/utils/PMTabs";
import { useUrlTab } from "../../../components/utils/useUrlTab";
import { CustomerInfo } from "../../../components/dashboard/customers/customer-info";
import PMLinkCard from "../../../components/utils/PMLinkCard";
import { TWclassNames } from "../../../components/utils/Div";
import {
  BuildingOffice2Icon,
  Cog6ToothIcon,
  UsersIcon,
  PaperClipIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  EllipsisVerticalIcon,
  LockClosedIcon,
  LockOpenIcon,
} from "@heroicons/react/24/outline";
import { ResourceEmpty } from "../../../components/utils/ResourceEmpty";
import { destroyCustomer, fetchCustomer } from "../../../api/customer";
import { axiosWithToast, mapErrors } from "../../../components/Utils";
import CaseDialog from "../../../components/dashboard/cases/case-dialog";
import { CasesStackList } from "../../../components/dashboard/cases/cases-stack-list";
import ServiceReportEditModal from "../../../components/dashboard/service/reports/service-report-edit-modal";
import { ReportsStackList } from "../../../components/dashboard/service/reports/reports-stack-list";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { ServiceAgreementsStackList } from "../../../components/dashboard/service/agreements/agreements-stack-list";
import { ProjectsStackList } from "../../../components/dashboard/projects/projects-stack-list";
import ServiceAgreementModal from "../../../components/dashboard/service/agreements/modal";
import ProjectCreateGuideDialog from "../../../components/dashboard/projects/ProjectCreateGuideDialog";
import { OrdersStacklist } from "../../../components/dashboard/orders/orders-stack-list";
import CustomerSystemsOverview from "../../../components/dashboard/customers/systems/CustomerSystemsOverview";
import SimplePrompt from "../../../components/utils/simplePrompt";
import { HistoriesModalButton } from "../../../components/dashboard/histories/modal-button";
import { CustomerLockBanner } from "../../../components/dashboard/customers/CustomerLockBanner";
import { CustomerLockDialog } from "../../../components/dashboard/customers/CustomerLockDialog";
import { useReportLockedCustomer } from "../../../contexts/CustomerLockTracker";
import { useUserHasAccess } from "../../../routes/roleOnly";
import JournalSection from "../../../components/dashboard/journaling/JournalSection";
import { StatusUpdateSection } from "../../../components/dashboard/statusUpdates/StatusUpdateSection";
import TextDocumentsSection from "../../../components/dashboard/text-documents/TextDocumentsSection";
import { SupplierProductsOverview } from "../../../components/dashboard/customers/SupplierProductsOverview";

export const Customer = () => {
  const [lockDialogOpen, setLockDialogOpen] = useState(false);
  const [caseDialogOpen, setCaseDialogOpen] = useState(false);
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const inventoryRoles = useSelector((state) => state.session.currentUser?.roles || []);
  const canReadSupplierProducts = inventoryRoles.some((role) =>
    ["admin", "inventory", "inventory_finance"].includes(role.name));

  const [itemState, setItemState] = useState({
    isLoading: true,
    error: false,
    errors: [],
    customer: {},
  });

  // Deconstruct itemState (Store)
  const { isLoading, error, errors, customer } = itemState;
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);

  const item = customer;
  const customerLocked = Boolean(item?.profile_locked);
  useReportLockedCustomer(id ? `customer-page-${id}` : null, item);
  const canEditCustomer = useUserHasAccess({ role: "customers", action: "edit" });
  const canCreateCustomer = useUserHasAccess({ role: "customers", action: "create" });
  const canDeleteCustomer = useUserHasAccess({ role: "customers", action: "delete" });

  const rightSideActions = [
    {
      label: "New Case",
      description: "Create a new case",
      path: "",
      action: () => setCaseDialogOpen(true),
      icon: PaperClipIcon,
      iconForeground: "text-teal-700",
      iconBackground: "bg-teal-50",
      active: !customerLocked,
    },
    {
      label: "Service Report",
      description: "Create a service report",
      path: "",
      action: () => setReportDialogOpen(true),
      icon: Cog6ToothIcon,
      iconForeground: "text-teal-700",
      iconBackground: "bg-teal-50",
      active: !customerLocked,
    },
    {
      label: "Service Agreement",
      description: "Create a service agreement",
      path: "",
      action: () => setServiceAgreementDialogOpen(true),
      icon: UsersIcon,
      iconForeground: "text-purple-700",
      iconBackground: "bg-purple-50",
      active: !customerLocked,
    },
    {
      label: "Project",
      description: "Create a project",
      path: "",
      action: () => setProjectDialogOpen(true),
      icon: BuildingOffice2Icon,
      iconForeground: "text-sky-700",
      iconBackground: "bg-sky-50",
      active: !customerLocked,
    },
  ];

  useEffect(() => {
    getCustomer();
  }, [dispatch, id]);

  const getCustomer = async () => {
    return fetchCustomer(id)
      .then((res) => {
        if (res.data.id) {
          setItemState((prevState) => ({
            ...prevState,
            isLoading: false,
            customer: res.data,
          }));
        }
      })
      .catch((err) => {
        let errors = null;
        if (err?.data?.errors) {
          errors = mapErrors(err.data.errors);
        } else {
          errors = ["Something went wrong"];
        }
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          customer: {},
          error: true,
          errors: errors,
        }));
      });
  };

  const handleRefresh = (data) => {
    setItemState((prevState) => ({
      ...prevState,
      customer: data,
      isLoading: false,
    }));
  };

  const handleLoading = () => {
    setItemState((prevState) => ({
      ...prevState,
      isLoading: true,
    }));
  };

  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [serviceAgreementDialogOpen, setServiceAgreementDialogOpen] =
    useState(false);

  const tabs = [
    {
      name: t("Details"),
      tabKey: "details",
      component: (
        <CustomerInfo
          item={item}
          onRefresh={(data) => handleRefresh(data)}
          onLoading={() => handleLoading()}
        />
      ),
    },
    {
      name: t("Oppdatering"),
      tabKey: "updates",
      component: (
        <StatusUpdateSection
          subjectType="customer"
          subjectId={item.id}
          customer={item}
        />
      ),
    },
    {
      name: t("Cases"),
      tabKey: "cases",
      component: (
        <CasesStackList
          module_param={item.id}
          module="customer"
          filters={[
            {
              name: "customer",
              value: item.id,
            },
          ]}
        />
      ),
    },
    {
      name: t("module_overview.tab.orders"),
      tabKey: "orders",
      component: (
        <OrdersStacklist
          module_param={item.id}
          module="customer"
          filters={[
            {
              name: "customer",
              value: item.id,
            },
          ]}
        />
      ),
    },
    {
      name: t("module_overview.tab.reports"),
      tabKey: "reports",
      component: (
        <ReportsStackList
          module_param={item.id}
          module="customer"
          showDeleted={true}
          fields="id, displayTitle, report_number, status, executing_employee, delivery_address, customer, report_type, appointment, description"
          filters={[
            {
              name: "customer",
              value: item.id,
            },
          ]}
        />
      ),
    },
    {
      name: t("customer.systems.tab"),
      tabKey: "facilities",
      component: (
        <CustomerSystemsOverview
          customer={item}
          onRefresh={() => handleRefresh(item)}
        />
      ),
    },
    ...(canReadSupplierProducts && ["supplier", "customer_company_supplier"].includes(item?.customer_type) ? [{
      name: t("supplierProducts.title"),
      tabKey: "supplier-products",
      component: <SupplierProductsOverview supplierId={item.id} />,
    }] : []),
    {
      name: t("module_overview.tab.agreements"),
      tabKey: "agreements",
      component: (
        <ServiceAgreementsStackList
          module_param={item.id}
          module="customer"
          filters={[
            {
              name: "customer",
              value: item.id,
            },
          ]}
        />
      ),
    },
    {
      name: t("module_overview.tab.projects"),
      tabKey: "projects",
      href: "#",
      component: (
        <ProjectsStackList
          module_param={item.id}
          module="customer"
          filters={[
            {
              name: "customer",
              value: item.id,
            },
          ]}
        />
      ),
    },
    {
      name: t("journal.journals"),
      tabKey: "journal",
      component: <JournalSection subjectType="Customer" subjectId={item.id} />,
    },
    {
      name: t("textDocuments.title"),
      tabKey: "documents",
      component: (
        <TextDocumentsSection subjectType="Customer" subjectId={item.id} />
      ),
    },
  ];

  const [selectedTab, setSelectedTab] = useUrlTab(tabs);
  const [deletePromptOpen, setDeletePromptOpen] = useState(false);

  const navigate = useNavigate();

  const removeCustomer = async (customer) => {
    axiosWithToast(
      destroyCustomer(customer),
      () => {},
      () => navigate("/dashboard/customers")
    );
  };

  const headerActions = [
    ...(canDeleteCustomer ? [{
      component: null,
      label: "Slett",
      description: "",
      path: null,
      action: () => setDeletePromptOpen(true),
      icon: TrashIcon,
      textColor: "text-red-700",
      bgColor: "bg-white",
      hidden: false,
      menu: true,
    }] : []),
    {
      component: (
        <HistoriesModalButton module="customer" module_param={customer?.id} />
      ),
      path: null,
      hidden: false,
      menu: false,
    },
    ...(canEditCustomer
      ? [
          {
            component: null,
            label: "Edit",
            description: "",
            path: `/dashboard/customers/${customer?.id}/edit`,
            icon: PencilIcon,
            textColor: "text-blue-700",
            bgColor: "bg-white",
            hidden: false,
            menu: false,
          },
        ]
      : []),
  ];

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="">
          <ResourceLoading />
        </div>
      );
    }
    if (error && errors) {
      return <ResourceError errors={errors} />;
    }

    if (!item?.id) {
      return <ResourceEmpty />;
    }

    const visibleHeaderActions = headerActions.filter(
      (action) => action.hidden === false && action.menu === false
    );
    const menuHeaderActions = headerActions.filter(
      (action) => action.hidden === false && action.menu === true
    );

    const customerSlimHeader = (
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-lg font-bold leading-tight text-gray-900 sm:text-xl">
            {customer?.customer_number + " " + customer?.name}
          </h1>
        </div>

        <div className="flex flex-shrink-0 items-center gap-2">
          {visibleHeaderActions.map((action, index) =>
            action.component ? (
              <Fragment key={index}>{action.component}</Fragment>
            ) : (
              <button
                key={index}
                type="button"
                onClick={
                  action.action
                    ? action.action
                    : action?.path?.substring(0, 4) === "http"
                    ? () => window.location.replace(action.path)
                    : () => navigate(action.path)
                }
                disabled={action.disabled || action.loading}
                title={action.label}
                aria-label={action.label}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {action.icon && (
                  <action.icon className="h-5 w-5" aria-hidden="true" />
                )}
              </button>
            )
          )}

          {canEditCustomer && (
            <button
              type="button"
              onClick={() => setLockDialogOpen(true)}
              className={`inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border px-3 text-sm font-semibold shadow-sm transition-colors ${
                customerLocked
                  ? "border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100"
                  : "border-red-200 bg-red-50 text-red-800 hover:bg-red-100"
              }`}
            >
              {customerLocked ? (
                <LockOpenIcon className="h-5 w-5" aria-hidden="true" />
              ) : (
                <LockClosedIcon className="h-5 w-5" aria-hidden="true" />
              )}
              {customerLocked
                ? t("customer.lock.unlock_action")
                : t("customer.lock.lock_action")}
            </button>
          )}

          {canCreateCustomer && (
            <button
              type="button"
              onClick={() => setCaseDialogOpen(true)}
              className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-blue-400 px-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <PlusIcon className="h-5 w-5" aria-hidden="true" />
              {t("New")}
            </button>
          )}

          {menuHeaderActions.length > 0 && (
            <Menu as="div" className="relative">
              <Menu.Button
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm transition-colors hover:bg-gray-50"
                aria-label={t("More")}
              >
                <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-150"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-100"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-50 mt-2 w-48 max-w-[min(12rem,calc(100vw-1.5rem))] origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  {menuHeaderActions.map((action, index) => (
                    <Menu.Item
                      key={index}
                      disabled={action.disabled || action.loading}
                    >
                      {({ disabled }) => (
                        <a
                          onClick={
                            disabled
                              ? undefined
                              : action.action
                              ? action.action
                              : action?.path?.substring(0, 4) === "http"
                              ? () => window.location.replace(action.path)
                              : () => navigate(action.path)
                          }
                          className={TWclassNames(
                            "flex cursor-pointer items-center px-4 py-3 text-sm",
                            action.textColor ? action.textColor : "text-gray-900",
                            disabled
                              ? "cursor-not-allowed opacity-50"
                              : "hover:bg-blue-100"
                          )}
                        >
                          {action.icon && (
                            <action.icon
                              className="mr-2 h-5 w-5 text-gray-400"
                              aria-hidden="true"
                            />
                          )}
                          {action.label}
                        </a>
                      )}
                    </Menu.Item>
                  ))}
                </Menu.Items>
              </Transition>
            </Menu>
          )}
        </div>
      </div>
    );

    return (
      <>
        <DashboardHeader
          variant="v2"
          sticky
          subtitle=""
          title={customer?.customer_number + " " + customer?.name}
          customHeader={customerSlimHeader}
        >
          <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
            <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
              <CustomerLockBanner customer={item} />
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="order-2 lg:order-1 lg:col-span-2">
                  <PMTabs tabs={tabs} selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
                  <div className="py-5">{tabs[selectedTab].component}</div>
                </div>
                <div className="order-1 lg:order-2 lg:col-span-1">
                  <PMLinkCard actions={rightSideActions} />
                </div>
              </div>

              <ServiceReportEditModal
                customer={item}
                open={reportDialogOpen}
                onClose={() => setReportDialogOpen(false)}
                selected={null}
                onRefresh={() => null}
              />

              <CaseDialog
                customer={item}
                module_param={item.id}
                module="customer"
                open={caseDialogOpen}
                onClose={() => setCaseDialogOpen(false)}
              />

              <ServiceAgreementModal
                open={serviceAgreementDialogOpen}
                onClose={() => setServiceAgreementDialogOpen(false)}
                customer={item}
              />

              <ProjectCreateGuideDialog
                open={projectDialogOpen}
                onClose={() => setProjectDialogOpen(false)}
                customer={item}
                navigateOnCreate={true}
              />
            </div>
          </div>
        </DashboardHeader>

        <SimplePrompt
          title="Sikker på at du vill lukke saken?"
          description="Kunden slettes permanent"
          onAccept={() => removeCustomer(item)}
          open={deletePromptOpen}
          onClose={() => setDeletePromptOpen(false)}
        />

        <CustomerLockDialog
          customer={item}
          open={lockDialogOpen}
          onClose={() => setLockDialogOpen(false)}
          onChanged={(data) => handleRefresh(data)}
        />
      </>
    );
  };

  return <>{renderContent()}</>;
};
